import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { getSelectedAvailabilityBakeryId } from '@theme/availability';

/**
 * Max pages the eager availability loader will pull in. Beyond this the PLP
 * degrades gracefully to converge-as-you-scroll (sentinel observers).
 * 8 pages ≈ 200 products at 24/page.
 */
const MAX_EAGER_PAGES = 8;

/**
 * Failsafe cap on how long the eager-loading veil waits for the Hydrogen
 * availability order to settle before revealing anyway (e.g. if
 * availability-grid-sort.js failed to load). Kept under the inline pre-paint
 * script's 8s failsafe in sections/main-collection.liquid.
 */
const SERVER_ORDER_SETTLE_TIMEOUT_MS = 5000;

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.#eagerAvailabilitySortEnabled) {
      window.addEventListener('store-selection:changed', this.#handleStoreSelectionChanged);
      // A selection may already exist from a previous session.
      this.#maybeEagerLoad();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    window.removeEventListener('store-selection:changed', this.#handleStoreSelectionChanged);
  }

  /**
   * Eager availability sort (Option C): stamped from Liquid
   * (sections/main-collection.liquid) only when infinite scroll is active
   * AND the merchant has the availability sort enabled.
   */
  get #eagerAvailabilitySortEnabled() {
    return this.dataset.eagerAvailabilitySort === 'true';
  }

  /**
   * Eagerly loads every remaining page so availability-grid-sort.js can
   * apply a TRUE GLOBAL sort instead of a per-loaded-page one, and holds the
   * loading veil up until BOTH the pages are loaded AND the Hydrogen
   * availability order has settled — so the grid is revealed already in the
   * server order rather than flashing Shopify's native order and reshuffling.
   *
   * Only meaningful when a bakery selection exists — for guests the pre-paint
   * veil is never stamped (there's no availability order), so this just clears
   * any residual state.
   *
   * While the work runs the grid carries a loading state (`data-eager-loading`
   * + `aria-busy`). The veil clears one frame AFTER the work completes so the
   * final rAF-coalesced sort pass paints while still covered.
   */
  #maybeEagerLoad() {
    if (!this.#eagerAvailabilitySortEnabled) return;

    // Is the pre-paint veil currently up? It's only stamped on the initial page
    // load (by the inline script in sections/main-collection.liquid), so this
    // distinguishes the first load — where we must hold the grid until the
    // Hydrogen order is applied — from later bakery changes.
    const veilPrepainted = this.hasAttribute('data-eager-loading');

    if (!getSelectedAvailabilityBakeryId()) {
      // Guest / no selection (or a stale/partial localStorage row) — there's no
      // availability order to wait for. Clear any residual veil state.
      this.#setEagerLoading(false);
      return;
    }

    const hasPages = this.#hasRemainingPages;

    // Nothing to hold the grid for: no pages to eager-load and no pre-paint veil
    // to resolve (e.g. a bakery change with everything already loaded). Leave
    // the grid in place — availability-grid-sort.js reorders to the new order on
    // its own — and don't flash the veil.
    if (!hasPages && !veilPrepainted) {
      this.#setEagerLoading(false);
      return;
    }

    this.#setEagerLoading(true);

    requestIdleCallback(async () => {
      try {
        // On the initial load, wait for the Hydrogen order to settle so a
        // single-page or already-loaded grid never reveals in native order and
        // reshuffles. Also load the remaining pages when there are any.
        const work = [];
        if (veilPrepainted) work.push(this.#waitForServerOrderSettled());
        if (hasPages) work.push(this.loadAllRemainingPages({ maxPages: MAX_EAGER_PAGES }));
        await Promise.all(work);
      } finally {
        // Double rAF: the grid sort schedules its reorder on a rAF from the
        // settle signal / final PageContentAppended — waiting two frames
        // guarantees the settled order is what gets revealed.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.#setEagerLoading(false));
        });
      }
    });
  }

  /**
   * Resolves once availability-grid-sort.js signals that the initial Hydrogen
   * server order has resolved (map applied, cleared, or failed). Resolves
   * immediately if that already happened before this element upgraded, on the
   * `availability:server-order-settled` event otherwise, or after a failsafe
   * timeout so a sort-module load failure can't strand the shopper behind the
   * veil.
   *
   * @returns {Promise<void>}
   */
  #waitForServerOrderSettled() {
    if (/** @type {any} */ (window).__availabilityServerOrderSettled) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        window.removeEventListener('availability:server-order-settled', finish);
        resolve();
      };
      window.addEventListener('availability:server-order-settled', finish, { once: true });
      setTimeout(finish, SERVER_ORDER_SETTLE_TIMEOUT_MS);
    });
  }

  /**
   * Whether the grid still has unloaded pages (in either direction) within
   * the eager cap. Mirrors the gates in loadAllRemainingPages so the
   * loading veil is only shown when there's actual work to do.
   */
  get #hasRemainingPages() {
    const { grid, cards } = this.refs;
    if (!grid || !Array.isArray(cards) || cards.length === 0) return false;

    const lastPage = Number(grid.dataset.lastPage);
    if (!Number.isFinite(lastPage) || lastPage <= 1 || lastPage > MAX_EAGER_PAGES) return false;

    const firstLoaded = Number(cards[0]?.dataset.page);
    const lastLoaded = Number(cards[cards.length - 1]?.dataset.page);
    return firstLoaded > 1 || lastLoaded < lastPage;
  }

  /**
   * @param {boolean} loading
   */
  #setEagerLoading(loading) {
    this.toggleAttribute('data-eager-loading', loading);

    const { grid } = this.refs;
    if (!grid) return;

    if (loading) {
      grid.setAttribute('aria-busy', 'true');
    } else {
      grid.removeAttribute('aria-busy');
    }
  }

  #handleStoreSelectionChanged = () => {
    this.#maybeEagerLoad();
  };

  /**
   * Filter updates replace the grid and invalidate the page cache —
   * re-trigger the eager load against the new result set.
   */
  onPagesInvalidated() {
    this.#maybeEagerLoad();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
